import { useQuery } from '@apollo/client/react/index.js';
import {
  AnimatedCounter,
  GlassCard,
  GlassCardContent,
  GlassCardHeader,
  GlassTable,
  GlassTableBody,
  GlassTableCell,
  GlassTableHead,
  GlassTableHeaderCell,
  GlassTableRow,
} from '@merritrac/shared/components';
import { BarChart2, Clock, Target, TrendingUp, Users } from 'lucide-react';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';

import { GET_SERVICE_ORDER_PERFORMANCE } from '../lib/graphql/operations.js';

import type { ReactElement } from 'react';

interface TechPerformance {
  technician: string;
  unitsCompleted: number;
  totalHours: number;
  averageMinutesPerUnit: number;
}

interface TimeSlot {
  unitLabel: string;
  startTime: string;
  endTime: string | null;
  durationMinutes: number;
  technician: string;
}

interface PerformanceData {
  serviceOrderPerformance: {
    totalUnits: number;
    completedUnits: number;
    accessDeniedUnits: number;
    totalTimeHours: number;
    estimatedDurationHours: number | null;
    averageMinutesPerUnit: number;
    technicianBreakdown: TechPerformance[];
    timeDistribution: TimeSlot[];
  };
}

interface Props {
  serviceOrderAk: number;
}

function KpiCard({
  icon: Icon,
  label,
  value,
  suffix = '',
  decimals = 0,
  color,
}: {
  icon: typeof Clock;
  label: string;
  value: number;
  suffix?: string;
  decimals?: number;
  color: string;
}): ReactElement {
  return (
    <GlassCard>
      <GlassCardContent>
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg bg-bg-subtle ${color}`}>
            <Icon size={18} />
          </div>
          <div>
            <div className="text-2xl font-bold text-text tabular-nums">
              <AnimatedCounter value={value} suffix={suffix} decimals={decimals} />
            </div>
            <div className="text-xs text-text-tertiary">{label}</div>
          </div>
        </div>
      </GlassCardContent>
    </GlassCard>
  );
}

export function ServiceOrderPerformance({ serviceOrderAk }: Props): ReactElement {
  const { data, loading } = useQuery<PerformanceData>(GET_SERVICE_ORDER_PERFORMANCE, {
    variables: { serviceOrderAk },
    skip: serviceOrderAk <= 0,
  });

  const perf = data?.serviceOrderPerformance;

  if (loading) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <GlassCard key={i}>
              <GlassCardContent>
                <div className="space-y-2">
                  <div className="skeleton h-8 w-20 rounded" />
                  <div className="skeleton h-3 w-16 rounded" />
                </div>
              </GlassCardContent>
            </GlassCard>
          ))}
        </div>
      </div>
    );
  }

  if (!perf) {
    return (
      <GlassCard>
        <GlassCardContent>
          <p className="text-sm text-text-secondary text-center py-6">
            No performance data available.
          </p>
        </GlassCardContent>
      </GlassCard>
    );
  }

  const completionRate =
    perf.totalUnits > 0
      ? Math.round(((perf.completedUnits + perf.accessDeniedUnits) / perf.totalUnits) * 100)
      : 0;

  const efficiency =
    perf.estimatedDurationHours !== null && perf.estimatedDurationHours > 0
      ? Math.round((perf.totalTimeHours / perf.estimatedDurationHours) * 100)
      : null;

  // Prepare chart data — aggregate by technician for the bar chart
  const techChartData = perf.technicianBreakdown.map((t) => ({
    name: t.technician,
    hours: t.totalHours,
    units: t.unitsCompleted,
    avgMin: t.averageMinutesPerUnit,
  }));

  return (
    <div className="space-y-5">
      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          icon={Clock}
          label="Total Time"
          value={perf.totalTimeHours}
          suffix=" hrs"
          decimals={1}
          color="text-primary"
        />
        <KpiCard
          icon={TrendingUp}
          label="Avg per Unit"
          value={perf.averageMinutesPerUnit}
          suffix=" min"
          decimals={1}
          color="text-info"
        />
        <KpiCard
          icon={Target}
          label="Completion Rate"
          value={completionRate}
          suffix="%"
          color="text-success"
        />
        <KpiCard
          icon={BarChart2}
          label={efficiency !== null ? 'Est. vs Actual' : 'Units Done'}
          value={efficiency ?? perf.completedUnits}
          suffix={efficiency !== null ? '%' : ''}
          color="text-warning"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        {/* Technician Hours Chart */}
        {techChartData.length > 0 && (
          <GlassCard>
            <GlassCardHeader>
              <h3 className="text-sm font-semibold text-text flex items-center gap-2">
                <Users size={14} />
                Time by Technician
              </h3>
            </GlassCardHeader>
            <GlassCardContent>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={techChartData} layout="vertical" margin={{ left: 10, right: 20 }}>
                    <CartesianGrid strokeDasharray="3 3" opacity={0.15} />
                    <XAxis type="number" tick={{ fontSize: 11 }} />
                    <YAxis dataKey="name" type="category" tick={{ fontSize: 11 }} width={80} />
                    <Tooltip
                      contentStyle={{
                        background: 'var(--glass-bg)',
                        border: '1px solid var(--glass-border)',
                        borderRadius: '8px',
                        backdropFilter: 'blur(12px)',
                        fontSize: '12px',
                      }}
                      formatter={(value: number, name: string) => {
                        if (name === 'hours') {
                          return [`${value.toFixed(1)} hrs`, 'Total Hours'];
                        }
                        return [value, name];
                      }}
                    />
                    <Bar dataKey="hours" fill="var(--primary)" radius={[0, 4, 4, 0]} barSize={20} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </GlassCardContent>
          </GlassCard>
        )}

        {/* Technician Breakdown Table */}
        {perf.technicianBreakdown.length > 0 && (
          <GlassCard>
            <GlassCardHeader>
              <h3 className="text-sm font-semibold text-text">Technician Breakdown</h3>
            </GlassCardHeader>
            <GlassCardContent>
              <GlassTable>
                <GlassTableHead>
                  <GlassTableRow>
                    <GlassTableHeaderCell>Technician</GlassTableHeaderCell>
                    <GlassTableHeaderCell>Units</GlassTableHeaderCell>
                    <GlassTableHeaderCell>Hours</GlassTableHeaderCell>
                    <GlassTableHeaderCell>Avg/Unit</GlassTableHeaderCell>
                  </GlassTableRow>
                </GlassTableHead>
                <GlassTableBody>
                  {perf.technicianBreakdown.map((tech) => (
                    <GlassTableRow key={tech.technician}>
                      <GlassTableCell>{tech.technician}</GlassTableCell>
                      <GlassTableCell>{tech.unitsCompleted}</GlassTableCell>
                      <GlassTableCell>{tech.totalHours.toFixed(1)}</GlassTableCell>
                      <GlassTableCell>{tech.averageMinutesPerUnit.toFixed(1)} min</GlassTableCell>
                    </GlassTableRow>
                  ))}
                </GlassTableBody>
              </GlassTable>
            </GlassCardContent>
          </GlassCard>
        )}
      </div>
    </div>
  );
}
